<template>
    <div class="register-page">
        <page-hero
            title="Εγγραφή ομάδας"
            eyebrow="The League"
            subtitle="Συμπλήρωσε τα στοιχεία της ομάδας και των παικτών. Θα επικοινωνήσουμε μαζί σου για την επιβεβαίωση."
            compact
        />

        <div class="cl-page">
            <v-form ref="form" v-model="valid" class="register-form" @submit.prevent="registerTeam">
                <!-- 1. the team -->
                <section class="register-card">
                    <header class="register-card__head">
                        <span class="register-step">1</span>
                        <span class="register-card__title">Στοιχεία ομάδας</span>
                    </header>
                    <div class="register-card__body">
                        <v-row dense>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="register.teamName"
                                    color="teal"
                                    label="Όνομα ομάδας *"
                                    outlined
                                    prepend-inner-icon="mdi-shield-outline"
                                    :rules="[rules.required]"
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="register.telephone"
                                    type="tel"
                                    maxlength="10"
                                    color="teal"
                                    label="Τηλέφωνο ομάδας *"
                                    outlined
                                    prepend-inner-icon="mdi-phone-outline"
                                    :rules="[rules.required, rules.phone]"
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="register.email"
                                    type="email"
                                    color="teal"
                                    label="Email ομάδας *"
                                    outlined
                                    prepend-inner-icon="mdi-email-outline"
                                    :rules="[rules.required, rules.email]"
                                />
                            </v-col>
                        </v-row>
                    </div>
                </section>

                <!-- 2. the players -->
                <section class="register-card">
                    <header class="register-card__head">
                        <span class="register-step">2</span>
                        <span class="register-card__title">Παίκτες</span>
                        <span class="register-card__count">{{ register.players.length }}</span>
                    </header>
                    <div class="register-card__body">
                        <div v-if="!register.players.length" class="register-empty">
                            Πρόσθεσε τους παίκτες της ομάδας σου.
                        </div>

                        <div v-for="(player, i) in register.players" :key="player.key" class="player-card">
                            <div class="player-card__head">
                                <span class="player-card__index">{{ i + 1 }}</span>
                                <span class="player-card__name">{{ playerTitle(player) }}</span>
                                <button type="button" class="player-card__remove" title="Διαγραφή παίκτη" @click="removePlayer(i)">
                                    <v-icon small>mdi-trash-can-outline</v-icon>
                                    <span>Αφαίρεση</span>
                                </button>
                            </div>
                            <v-row dense class="player-card__fields">
                                <v-col cols="12" md="6">
                                    <v-text-field v-model="player.name" color="teal" label="Ονοματεπώνυμο *" outlined dense :rules="[rules.required]" />
                                </v-col>
                                <v-col cols="12" sm="6" md="3">
                                    <v-text-field v-model="player.birthday" type="date" color="teal" label="Ημερομηνία γέννησης" outlined dense />
                                </v-col>
                                <v-col cols="12" sm="6" md="3">
                                    <v-text-field
                                        v-model="player.mobile"
                                        type="tel"
                                        maxlength="10"
                                        color="teal"
                                        label="Κινητό"
                                        outlined
                                        dense
                                        :rules="[rules.mobile]"
                                    />
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-text-field v-model="player.email" type="email" color="teal" label="Email" outlined dense :rules="[rules.email]" />
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-text-field v-model="player.residence" color="teal" label="Κατοικία" outlined dense />
                                </v-col>
                                <v-col cols="12" sm="4">
                                    <v-select
                                        v-model="player.position"
                                        :items="positions"
                                        color="teal"
                                        item-color="teal"
                                        label="Θέση"
                                        outlined
                                        dense
                                        clearable
                                    />
                                </v-col>
                                <v-col cols="6" sm="4">
                                    <v-text-field v-model="player.jersey" type="number" min="0" color="teal" label="Αριθμός φανέλας" outlined dense />
                                </v-col>
                                <v-col cols="6" sm="4">
                                    <v-text-field v-model="player.height" type="number" min="0" color="teal" label="Ύψος" suffix="cm" outlined dense />
                                </v-col>
                            </v-row>
                        </div>

                        <button type="button" class="add-player" @click="addPlayer">
                            <v-icon>mdi-account-plus-outline</v-icon>
                            Προσθήκη παίκτη
                        </button>
                    </div>
                </section>

                <!-- 3. the rules and the submit -->
                <section class="register-submit">
                    <v-checkbox v-model="terms" color="teal" hide-details class="mt-0 pt-0">
                        <template v-slot:label>
                            <span class="register-terms">
                                Αποδέχομαι τον
                                <router-link :to="{ name: 'Regulations' }" target="_blank" @click.native.stop>κανονισμό του τουρνουά</router-link>
                            </span>
                        </template>
                    </v-checkbox>
                    <v-btn
                        type="submit"
                        :disabled="!terms || !valid"
                        :loading="submitting"
                        large
                        depressed
                        class="custom-teal white--text register-submit__btn"
                    >
                        <v-icon left>mdi-check-circle-outline</v-icon>
                        Ολοκλήρωση εγγραφής
                    </v-btn>
                </section>
            </v-form>
        </div>
        <custom-dialog :dialog="showDialog" :title="title" :message="message" @update="closeDialog" />
    </div>
</template>

<script>
    import PageHero from '@/components/PageHero'
    import teamService from '@/services/team'
    import CustomDialog from '@/components/Dialog'

    let playerKey = 0

    function emptyPlayer() {
        playerKey += 1
        return {
            key: playerKey,
            // the full name: the players of the league have no separate surname field here
            name: null,
            residence: null,
            mobile: null,
            email: null,
            height: null,
            jersey: null,
            position: null,
            birthday: null
        }
    }

    function emptyRegister() {
        return { teamName: null, telephone: null, teamLogo: null, email: null, players: [emptyPlayer()] }
    }

    export default {
        components: { PageHero, CustomDialog },
        data() {
            return {
                register: emptyRegister(),
                terms: false,
                valid: false,
                submitting: false,
                showDialog: false,
                message: '',
                title: '',
                positions: [
                    { text: 'Guard', value: 'guard' },
                    { text: 'Forward', value: 'forward' },
                    { text: 'Center', value: 'center' }
                ],
                rules: {
                    required: value => !!value || 'Υποχρεωτικό πεδίο',
                    email: value => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || 'Μη έγκυρο email',
                    // Greek landline (2xxxxxxxxx) or mobile (69xxxxxxxx)
                    phone: value => !value || /^(2\d{9}|69\d{8})$/.test(value) || 'Μη έγκυρο τηλέφωνο (10 ψηφία)',
                    mobile: value => !value || /^69\d{8}$/.test(value) || 'Μη έγκυρο κινητό (69xxxxxxxx)'
                }
            }
        },
        methods: {
            playerTitle(player) {
                return (player.name || '').trim() || 'Νέος παίκτης'
            },
            addPlayer() {
                this.register.players.push(emptyPlayer())
            },
            removePlayer(index) {
                this.register.players.splice(index, 1)
            },
            registerTeam() {
                if (!this.terms || !this.$refs.form.validate()) {
                    return
                }
                const payload = {
                    ...this.register,
                    players: this.register.players.map(({ key, ...player }) => player)
                }
                this.submitting = true
                teamService.register(payload).then(() => {
                    this.showDialog = true
                    this.message = 'Καλή τύχη στο νέο πρωτάθλημα!'
                    this.title = 'Η εγγραφή σας ήταν επιτυχής!'
                    this.register = emptyRegister()
                    this.terms = false
                    this.$refs.form.resetValidation()
                }).catch(() => {
                    this.showDialog = true
                    this.message = 'Υπάρχει ήδη αίτηση με αυτό το όνομα ομάδας. Αν το πρόβλημα συνεχίζεται, επικοινώνησε μαζί μας.'
                    this.title = 'Η εγγραφή σας ήταν ανεπιτυχής!'
                }).finally(() => {
                    this.submitting = false
                })
            },
            closeDialog() {
                this.showDialog = false
                this.message = ''
                this.title = ''
            }
        }
    }
</script>

<style scoped>
.register-form {
    max-width: 1080px;
    margin: 0 auto;
}

/* the fields read in Roboto, not in the display font of the site */
.register-form ::v-deep .v-label,
.register-form ::v-deep input,
.register-form ::v-deep .v-select__selection,
.register-form ::v-deep .v-messages,
.register-form ::v-deep .v-text-field__suffix {
    font-family: Roboto, Arial, sans-serif !important;
}

/* ---------- a step of the form ---------- */
.register-card {
    margin-bottom: 22px;
    border-radius: 12px;
    background: #fff;
    border: 1px solid rgba(35, 35, 35, 0.08);
    box-shadow: 0 6px 20px rgba(35, 35, 35, 0.06);
    overflow: hidden;
}

.register-card__head {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 20px;
    background: #232323;
    color: #fff;
}

.register-card__head::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 3px;
    background: linear-gradient(90deg, #03e5b7 0%, #037ade 74%);
}

.register-step {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.3rem;
    transform: skewX(-8deg);
}

.register-card__title {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.6rem;
    line-height: 1;
    letter-spacing: 0.02em;
    text-transform: uppercase;
}

.register-card__count {
    padding: 1px 9px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.12);
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.78rem;
    font-weight: 700;
}

.register-card__body {
    padding: 22px 20px 10px;
}

.register-empty {
    margin-bottom: 14px;
    font-family: Roboto, Arial, sans-serif;
    color: rgba(35, 35, 35, 0.6);
    text-align: left;
}

/* ---------- a player ---------- */
.player-card {
    margin-bottom: 14px;
    border-radius: 10px;
    border: 1px solid rgba(35, 35, 35, 0.1);
    overflow: hidden;
}

.player-card__head {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px 8px 12px;
    background: #f6f8f8;
    border-bottom: 1px solid rgba(35, 35, 35, 0.08);
    border-left: 4px solid #009688;
}

.player-card__index {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 26px;
    border-radius: 6px;
    background: #232323;
    color: #fff;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.05rem;
}

.player-card__name {
    flex: 1 1 auto;
    min-width: 0;
    font-family: Roboto, Arial, sans-serif;
    font-weight: 700;
    color: #232323;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.player-card__remove {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 6px;
    color: #c62828;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.8rem;
    font-weight: 700;
}

.player-card__remove .v-icon {
    color: inherit !important;
}

.player-card__remove:hover {
    background: rgba(198, 40, 40, 0.08);
}

.player-card__fields {
    padding: 14px 12px 0;
}

.add-player {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    margin-bottom: 12px;
    padding: 14px;
    border: 2px dashed rgba(0, 150, 136, 0.5);
    border-radius: 10px;
    background: rgba(3, 229, 183, 0.05);
    color: #00796b;
    font-family: Roboto, Arial, sans-serif;
    font-weight: 700;
    transition: background 0.15s, border-color 0.15s;
}

.add-player .v-icon {
    color: inherit !important;
}

.add-player:hover {
    background: rgba(3, 229, 183, 0.12);
    border-color: #009688;
}

/* ---------- submit ---------- */
.register-submit {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
    padding: 18px 20px;
    border-radius: 12px;
    background: #232323;
    color: #fff;
}

.register-terms {
    font-family: Roboto, Arial, sans-serif;
    font-weight: 600;
    color: #fff;
}

.register-terms a {
    color: #03e5b7;
}

.register-submit ::v-deep .v-input--checkbox .v-icon {
    color: rgba(255, 255, 255, 0.7);
}

.register-submit__btn {
    font-family: Roboto, Arial, sans-serif;
    font-weight: 700;
    text-transform: none;
    letter-spacing: normal;
}

.register-submit__btn.v-btn--disabled {
    background: transparent !important;
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: rgba(255, 255, 255, 0.6) !important;
}

.register-submit__btn.v-btn--disabled .v-icon {
    color: rgba(255, 255, 255, 0.6) !important;
}

@media (max-width: 600px) {
    .register-card__body {
        padding: 16px 12px 6px;
    }

    .player-card__remove span {
        display: none;
    }

    .register-submit__btn {
        width: 100%;
    }
}
</style>
